<script lang="ts">
import ComplaintChat from './chat.vue';
import AttachUploader from '@/app/common/components/AttachUploader.vue';
import AlertDialog from '@/app/common/components/AlertDialog.vue';
import { ComplaintActivityLog, ComplaintAirlineInfo, ComplaintInfo } from '@/interface/Complaint.interface';
import { BaseConfirm } from '@/interface/BaseConfirm.interface';
import { complaintService, fileService, userService } from '@/services/serviceProvider';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { ATTACH_TYPE, FILE_COUNT_LIMIT, REQUEST_TYPE } from '@/app/const';
import { ref } from 'vue';

export default {
    components: {
        ComplaintChat,
        AttachUploader,
        AlertDialog
    },
    setup() {
        const fileSv = fileService;
        const airlineAnswerForm = ref();
        return { fileSv, airlineAnswerForm };
    },
    props: {
        modelValue: {
            type: Object,
            default: {},
        },
        uploadable: {
            type: Boolean,
            default: false
        },
        complaint: {
            type: Object,
            default: {}
        },
        viewMode: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'update:complaint'],
    data() {
        return {
            chatModal: {
                display: false
            } as BaseConfirm,
            failAlert: {
                display: false,
                success: false
            } as BaseAlert,
            request: this.complaint as ComplaintInfo,
            fileCountLimit: FILE_COUNT_LIMIT,
            selectedAirline: undefined as string | undefined,
            rules: {
                required: (message: any) => {
                    return (v: any) => !!v || message;
                },
            },
        }
    },
    computed: {
        model: {
            get() {
                return this.modelValue as ComplaintAirlineInfo;
            },
            set(newValue: ComplaintAirlineInfo) {
                this.$emit('update:modelValue', newValue);
            }
        },
        isMainDpm(): boolean {
            return this.request.relatedMainDepartment?.departmentCode === userService.currentDepartment();
        },
        isSubDpm(): boolean {
            const dpmCodes = this.request.relatedSubDepartments?.map(o => o.departmentCode);
            const currentDpm = userService.currentDepartment();
            if (dpmCodes && currentDpm) return dpmCodes.includes(currentDpm);
            return false;
        },
        allLogs() {
            const activitiesLogs = this.request.activitiesLogs || [];
            activitiesLogs.forEach(o => o.logType = 'activity');
            let chatLogs = this.request.chatLogs || [];
            let airlineUsername = userService.currentUserInfo()?.username;
            // หา department code ที่เกี่ยวข้องกับสายการบินที่กำลังทำงานอยู่
            let departmentCode = undefined as string | undefined;
            if (this.request.relatedMainDepartment?.external?.externalDepartment === airlineUsername) {
                departmentCode = this.complaint.relatedMainDepartment.departmentCode;
            } else if (this.request.relatedSubDepartments?.find(o => o.external?.externalDepartment === airlineUsername)) {
                departmentCode = this.complaint.relatedSubDepartments?.find((o: any) => o.external?.externalDepartment === airlineUsername)?.departmentCode;
            }
            // หากได้ department code แล้วก็เอาไปกรอง chat ออกมาเฉพาะคนที่กำลังคุยกับสายการบินนี้
            if (departmentCode)
                chatLogs = chatLogs.filter(o => o.departmentCode === departmentCode ||
                    o.createBy === userService.currentUserInfo()?.username);
            chatLogs.forEach(o => o.logType = 'chat');
            return activitiesLogs.concat(chatLogs);
        },
        sortedAllLogs() {
            return this.allLogs.sort((a, b) => (a.createDate && b.createDate ? a.createDate > b.createDate ? -1 : 1 : 1));
        },
        unreadChatCount() {
            const chatLogs = this.sortedAllLogs.filter(o => o.logType === 'chat');
            if (chatLogs && chatLogs.length > 0) {
                if (userService.isAsc() || userService.isDpm()) {
                    let externalDepartment = undefined as string | undefined;
                    if (this.isMainDpm) {
                        let dpm = this.request.relatedMainDepartment;
                        externalDepartment = dpm?.external?.externalDepartment
                    } else if (this.isSubDpm) {
                        let dpm = this.request.relatedSubDepartments?.find(o => o.departmentCode === userService.currentDepartment());
                        externalDepartment = dpm?.external?.externalDepartment;
                    }
                    return chatLogs.filter(o => !o.departmentCode && !o.read && o.createBy === externalDepartment)?.length;
                } else if (userService.isAirline()) {
                    let externalDepartment = userService.currentUserInfo()?.username;
                    let dpmCode = undefined as string | undefined;
                    if (this.request.relatedMainDepartment?.external?.externalDepartment === externalDepartment) {
                        dpmCode = this.request.relatedMainDepartment?.departmentCode;
                    } else if (this.request.relatedSubDepartments?.find(o => o.external?.externalDepartment === externalDepartment)) {
                        dpmCode = this.request.relatedSubDepartments?.find(o => o.external?.externalDepartment === externalDepartment)?.departmentCode;
                    }
                    return chatLogs.filter(o => o.departmentCode && !o.read && o.departmentCode === dpmCode)?.length;
                }
            }
            return 0;
        },
        headers() {
            return [
                { title: this.$t('t-order'), sortable: false, key: 'index', align: 'center', width: '5%' },
                { title: this.$t('t-file-name'), key: 'fileName' },
                { title: '', key: 'actions', sortable: false, width: '2%' },
            ] as any[];
        },
        isOcpbRequest() {
            return this.request.requestType === REQUEST_TYPE.OCPB;
        },
        isOpmRequest() {
            return this.request.requestType === REQUEST_TYPE.OPM;
        },
    },
    methods: {
        async sendChat(log: ComplaintActivityLog) {
            try {
                if (this.request.requestNo) {
                    await complaintService.saveChat(this.request.requestNo, { detail: log.detail })
                    if (!this.request.chatLogs) this.request.chatLogs = [];
                    this.request.chatLogs.push(log);
                    this.$emit('update:complaint', this.request);
                }
            } catch (e) {
                this.failAlert.messageTitle = this.$t('t-complaint-chat-send-message-fail');
                this.failAlert.display = true;
            }
        },
        allAirlineAttachments(): any[] {
            let newUploads = this.model.attachUploads as any[] ?? [];
            newUploads.forEach(up => { up.type = ATTACH_TYPE.NEW });
            let alreadySaved = this.model.externalDepartmentAttachments as any[] ?? [];
            alreadySaved.forEach(saved => { saved.type = ATTACH_TYPE.SAVED });
            return newUploads.concat(alreadySaved);
        },
        deleteAirlineAttachment(attach: any) {
            let allAttachs = this.allAirlineAttachments();
            if (allAttachs && allAttachs.length > 0) {
                if (attach.type === ATTACH_TYPE.NEW) {
                    let newUploads = this.model.attachUploads;
                    this.model.attachUploads = newUploads?.filter(o => o.name !== attach.name);
                } else if (attach.type === ATTACH_TYPE.SAVED) {
                    let saved = this.model.externalDepartmentAttachments;
                    this.model.externalDepartmentAttachments = saved?.filter(o => o.fileName !== attach.fileName);
                }
            }
        },
        checkDuplicateFileName() {
            let fileNameList = [] as string[];
            let allAttach = this.allAirlineAttachments();
            let allFilesUser = allAttach.map(o => {
                if (o.type === ATTACH_TYPE.NEW) {
                    return o.name;
                } else if (o.type === ATTACH_TYPE.SAVED) {
                    return o.fileName;
                }
            })
            allFilesUser.forEach(fileName => {
                if (fileNameList.includes(fileName)) {
                    this.failAlert = {
                        display: true,
                        messageTitle: this.$t('t-cannot-duplicate-file-name'),
                        messageText: this.$t('t-please-change-file-name'),
                    } as BaseAlert;
                    let index = this.model.attachUploads?.findIndex(o => o.name === fileName) as number;
                    if (index >= 0) {
                        this.model.attachUploads?.splice(index, 1);
                    }
                    return;
                } else {
                    fileNameList.push(fileName);
                }
            })
        },
        overSizeLimit() {
            this.failAlert = {
                display: true,
                messageTitle: this.$t('t-cannot-upload-over-size-limit'),
                messageText: this.$t('t-please-select-another-file'),
            } as BaseAlert;
        },
        errorEmptyFile() {
            this.failAlert = {
                display: true,
                messageTitle: this.$t("t-attachment-error-0byte"),
                messageText: this.$t('t-please-select-another-file'),
            } as BaseAlert;
        },
        async validateForm() {
            const res = this.airlineAnswerForm ? await this.airlineAnswerForm.validate() : true;
            return res.valid === undefined ? true : res.valid;
        },
        openAirlineChat() {
            this.selectedAirline = userService.currentUserInfo()?.username;
            this.chatModal.display = true;
            this.readChat();
        },
        async readChat() {
            try {
                if (this.request.requestNo) {
                    await complaintService.readChat(this.request.requestNo)
                }
            } catch (e) {
                this.failAlert = {
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-complaint-chat-read-message-fail'),
                } as BaseAlert
            }
        }
    }
}
</script>
<template>
    <v-card class="app-card admin-card" :class="isOcpbRequest || isOpmRequest ? 'mb-0' : ''">
        <div class="d-flex align-center w-100">
            <v-toolbar density="compact" :class="!uploadable ? 'mr-2' : 'mr-6'" style="width: fit-content;">
                <v-toolbar-title v-if="!uploadable" class="text-label-large mb-0">
                    {{ $t('t-airlines') }} {{ model.externalDepartmentName }}
                </v-toolbar-title>
                <v-toolbar-title v-else class="text-label-large mb-0">
                    {{ $t('t-complaint-airline-answer-back') }}
                </v-toolbar-title>
            </v-toolbar>
            <v-chip v-if="!uploadable" variant="flat" density="compact" class="gradient mr-6">
                {{ $t('t-complaint-airline-answer-already') }}
            </v-chip>
            <div v-if="uploadable" class="splitter my-3"></div>
            <div v-if="uploadable" class="mx-6 d-flex align-center">
                <v-icon icon="mdi mdi-forum-outline" size="small" class="mr-2"></v-icon>
                <span class="mr-2">{{ $t('t-complaint-chat') }}</span>
                <v-avatar v-if="unreadChatCount > 0" color="error" size="18" class="mr-2">
                    <span style="font-size: 12px;">{{ unreadChatCount }}</span>
                </v-avatar>
                <v-icon icon="mdi mdi-chevron-right" @click="openAirlineChat"></v-icon>
            </div>
        </div>
        <v-divider></v-divider>
        <v-card-text>
            <v-form ref="airlineAnswerForm" validate-on="submit lazy">
                <div class="text-label mr-2 mt-1">{{ $t('t-complaint-remark-short') }}</div>
                <v-textarea v-model="model.externalDepartmentAnswer" class="hide-float-label" variant="outlined"
                    :readonly="!uploadable || viewMode" v-bind:placeholder="$t('t-complaint-search-input-text')"
                    validate-on="input" hide-details="auto" :rules="[rules.required(`${$t('t-please-input')}`)]">
                </v-textarea>
                <div class="textarea-export formatted-new-line mt-2" style="display: none;">
                    {{ model.externalDepartmentAnswer ?? '-' }}
                </div>
            </v-form>
        </v-card-text>
        <v-card-text>
            <div class="text-label mr-2 mt-1">{{ $t('t-complaint-airline-answer-attach') }}</div>
            <v-row>
                <v-col v-if="uploadable && !viewMode" cols="12" style="min-height: 130px;">
                    <AttachUploader v-model="model.attachUploads" fileType="attach"
                        :disabled="!uploadable || allAirlineAttachments().length >= fileCountLimit"
                        @on-upload-success="checkDuplicateFileName" @on-over-size-limit="overSizeLimit"
                        @on-error-empty-file="errorEmptyFile">
                    </AttachUploader>
                </v-col>
                <v-col v-if="uploadable && !viewMode" cols="12">
                    <v-alert v-if="allAirlineAttachments() && allAirlineAttachments().length > 0" border="start"
                        border-color="primary" :title="$t('t-upload-file-limit')" density="compact" variant="tonal"
                        color="primary">
                    </v-alert>
                </v-col>
                <v-col cols="12">
                    <v-data-table :headers="headers" :items="allAirlineAttachments()" hide-default-footer
                        :no-data-text="$t('t-no-data-available')" :items-per-page="-1">
                        <template #[`item.index`]="{ index }">
                            <div class="w-100 text-center">{{ index + 1 }}</div>
                        </template>
                        <template #[`item.fileName`]="{ item }">
                            <span v-if="fileSv.isNewAttachment(item.type)">{{ item.name }}</span>
                            <span v-if="fileSv.isSavedAttachment(item.type)">{{ item.fileName }}</span>
                        </template>
                        <template v-slot:item.actions="{ item, index }">
                            <div class="actions d-flex align-center">
                                <v-btn variant="text" color="primary" @click="fileSv.viewFileInEditMode(item)">
                                    <v-icon icon="mdi mdi-eye-outline" class="mr-1"></v-icon>
                                    <span>{{ $t('t-view-doc') }}</span>
                                </v-btn>
                                <v-btn v-if="uploadable && !viewMode" variant="text" color="error"
                                    @click="deleteAirlineAttachment(item)">
                                    <v-icon icon="mdi mdi-close" class="mr-1"></v-icon>
                                    <span>{{ $t('t-delete') }}</span>
                                </v-btn>
                            </div>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
    <ComplaintChat v-model="chatModal.display" :complaint="request" :view-mode="viewMode"
        :filter-external-department="selectedAirline" @on-confirm="sendChat">
    </ComplaintChat>
    <!-- Fail -->
    <AlertDialog v-model="failAlert.display" :header="failAlert.header" :title="failAlert.messageTitle"
        :text="failAlert.messageText" :success="false">
    </AlertDialog>
</template>